// The conductor. Boots audio behind the Enter gesture, wires the generative engine,
// the lookahead scheduler, the voice allocator, the renderer, and the UI. In GENERATIVE
// mode the scheduler fires autonomous intents; in PLAYABLE mode autonomous voices pause
// but the field keeps drifting. Pointer picks always strike (you can touch it either way).
import { bootAudio, type BootedAudio } from './audioBoot';
import { resolveTier, type ResolvedTier } from './qualityTier';
import { Scheduler } from './scheduler';
import { VoiceAllocator } from './voiceAllocator';
import { createEnterOverlay, createGardenPanel, type PanelHandle } from './ui';
import { createStudioPanel, type StudioHandle } from './studio';
import { DiatomMixer } from './diatomMixer';
import { listScenes, saveScene, getScene, parseScene, type Scene } from './scenes';
import { Renderer, type PickEvent } from '../visuals/Renderer';
import { GenerativeEngine, SCALES } from '../generative';
import { DIATOMS, byId } from '../diatoms';
import { ENGINE_BUDGETS } from '@shared/voice';
import type { StrikeIntent } from '@shared/intents';
import { mulberry32 } from '@shared/rng';
import { PRESETS, DEFAULT_PRESET, pickExcitation, presetDrive, type Preset } from './presets';
import { multihitHits } from './multihit';
import { BANKS, NO_BANK, getBank, bankNumericId, loadBankBuffer } from './soundBanks';
import { SamplePlayer } from './samplePlayer';
import { DeviceTilt } from './deviceMotion';
import { MidiController } from './midi';

// Feed-forward makeup leveler constants (see App.updateLeveler).
// OUT_CEILING is the target peak at the *bank tap* (pre-makeup ×). It's set below 1 by a healthy
// margin on purpose: the dry bank, its convolution reverb, and the echo return all derive from the
// leveled signal and sum downstream, so the post-everything master peak runs higher than the tap.
// Measured: a sustained drone + echo reaches steady-state master peak ~1.0 (3.4% soft-clip) at 0.7,
// ~0.82 at 0.6, and ~0.75 at 0.5 — so a lower ceiling buys extra room on the LOUDEST signals (the
// drive-1.0 energetic scenes were still touching the soft-clip at 0.6). Crucially this only affects
// loud moments: sparse/quiet strikes have a tiny bus peak, so makeup = min(46, ceiling/peak) picks 46
// regardless of the ceiling — they stay exactly as loud. The ceiling pulls down only the hot sums.
const OUT_CEILING = 0.5; // target bank-tap peak; headroom for reverb+echo sum AND the loudest scenes
// Floor for the bus-peak divisor. MUST stay below OUT_CEILING/makeupBase (0.5/46 ≈ 0.0109) so that a
// near-silent bus yields makeup = min(46, ceiling/floor) = 46 — i.e. sparse/quiet presets keep the
// full reference makeup and the ceiling only ever PULLS DOWN loud sums, never attenuates quiet ones.
const BUS_PEAK_FLOOR = 0.008;
const BUS_PEAK_RELEASE = 0.05; // per-tick release coefficient for the bus peak follower (slow decay)

export class App {
  private tier: ResolvedTier;
  private audio: BootedAudio | null = null;
  private engine: GenerativeEngine;
  private renderer: Renderer;
  private scheduler: Scheduler | null = null;
  private allocator: VoiceAllocator | null = null;
  private panel: PanelHandle | null = null;
  private studio: StudioHandle | null = null;
  private voiceToInstance = new Map<number, number>();
  private autonomous = true;
  private rng = mulberry32(0x0cea2);
  private lastMood = '';
  private preset: Preset = DEFAULT_PRESET;
  private bowedVoices = new Set<number>();
  // studio mixer: per-diatom mute/solo/gain/excitation state (master volume is separate).
  private readonly mixer = new DiatomMixer();
  private masterVolume = 1;
  private bowTimeouts = new Map<number, ReturnType<typeof setTimeout>>();
  private dialVals = { calmActive: 0.5, darkBright: 0.5, pureRough: 0.5, roaming: 0.5 };
  // granular excitation: which sound bank is active (NO_BANK = grain events fall back to noise),
  // and which banks' buffers we've already shipped to the engine (load once, then just re-select).
  private activeBank = NO_BANK;
  private loadedBanks = new Set<string>();
  // grain-engine tuning (matches the kernel *_DEFAULT consts + the UI slider defaults)
  private grainTuning = { targetRms: 0.05, grainMs: 55, cloudGain: 0.6, cloudMs: 120 };
  // non-granular bank playback (ambient bed + direct sampler one-shots)
  private samplePlayer: SamplePlayer | null = null;
  private bankAudio = new Map<string, AudioBuffer>(); // cached AudioBuffer per loaded bank
  private ambientLevel = 0;
  private tilt: DeviceTilt | null = null; // device tilt/shake, only while gravity is enabled
  private gravityAvailable = false; // motion sensors offered (mobile / live sensor)
  private gravityOn = false; // user/preset has enabled tilt gravity
  private readonly midi = new MidiController(); // Web MIDI + learn (CC -> controls)
  private midiEnabled = false;
  private midiLearnMode = false;
  private driveVal = 1; // master drive (scales the leveler's output ceiling); set per-preset, overridable
  // Feed-forward makeup leveler: tap the pre-makeup bus and cap output at a ceiling, so loud presets
  // (few-but-loud resonant voices, or many) don't blow out — adapts to per-voice loudness AND count.
  private busTap: AnalyserNode | null = null;
  private busTapBuf: Float32Array<ArrayBuffer> | null = null;
  private busPeakFollow = 0;
  private lastMakeup = 46;

  constructor(private readonly root: HTMLElement) {
    this.tier = resolveTier();
    this.engine = new GenerativeEngine({ diatoms: DIATOMS, seed: 0xd1a70 });
    this.renderer = new Renderer({
      tier: this.tier.visual,
      reducedMotion: this.tier.reducedMotion,
      seed: 0x5eed,
    });
  }

  mount(): void {
    const overlay = createEnterOverlay(() => this.start());
    this.root.appendChild(overlay);
    window.addEventListener('beforeunload', this.onUnload, { once: true });
  }

  private readonly onUnload = (): void => this.dispose();

  private disposed = false;

  /** Release everything: timers, device listeners, audio nodes, the AudioContext, and the renderer. Idempotent. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    // Stop the scheduler FIRST so no tick runs against torn-down audio nodes.
    this.scheduler?.stop();
    for (const t of this.bowTimeouts.values()) clearTimeout(t);
    this.bowTimeouts.clear();
    this.tilt?.stop();
    this.samplePlayer?.dispose();
    this.renderer.destroy();
    if (this.audio) {
      this.audio.bank.dispose();
      this.audio.ocean.dispose();
      void this.audio.ctx.close().catch(() => {});
      // Null it so any in-flight setTimeout callbacks (welcome / multihit) hit the `!this.audio`
      // guard and no-op instead of touching the closed context.
      this.audio = null;
    }
  }

  /**
   * Decide whether to OFFER device gravity (the panel's Gravity button). Gravity is opt-in, not
   * always-on: we reveal the button on touch devices, or on any device once real orientation data
   * actually arrives. No permission is requested here.
   */
  private detectMotion(): void {
    if (this.tier.reducedMotion || !DeviceTilt.supported()) return;
    const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    if (coarse) this.markMotionAvailable();
    // Also reveal if a real sensor fires (Android / sensor-equipped desktops; iOS needs permission
    // first, so coarse-pointer is the signal there).
    const probe = (e: DeviceOrientationEvent): void => {
      if (e.beta !== null || e.gamma !== null) {
        this.markMotionAvailable();
        window.removeEventListener('deviceorientation', probe);
      }
    };
    window.addEventListener('deviceorientation', probe, { passive: true });
    setTimeout(() => window.removeEventListener('deviceorientation', probe), 4000);
  }

  /** Reveal the MIDI button (Chromium) + wire incoming CCs to controls; restore saved bindings. */
  private setupMidi(): void {
    if (!MidiController.supported()) return;
    this.midi.setHandlers({
      onValue: (id, v01) => this.panel?.applyMidi(id, v01),
      onLearned: (id, cc) => this.panel?.setMidiBinding(id, cc), // stays in learn mode for more
    });
    this.panel?.setMidiAvailable(true);
    for (const [id, cc] of this.midi.allBindings()) this.panel?.setMidiBinding(id, cc);
  }

  /** First tap enables Web MIDI (permission), then taps toggle learn mode. */
  private async toggleMidi(): Promise<void> {
    if (!this.midiEnabled) {
      this.midiEnabled = await this.midi.enable();
      if (!this.midiEnabled) return; // denied/unsupported
    }
    this.midiLearnMode = !this.midiLearnMode;
    if (!this.midiLearnMode) {
      this.midi.cancelLearn();
      this.panel?.setMidiLearning(null);
    }
    this.panel?.setMidiLearnMode(this.midiLearnMode);
  }

  private markMotionAvailable(): void {
    if (this.gravityAvailable) return;
    this.gravityAvailable = true;
    this.panel?.setGravityAvailable(true);
  }

  /**
   * Turn device-tilt gravity (+ shake-scatter) on/off. On enable, requests iOS permission (must be
   * called from a user gesture: the Gravity button or a preset-select). No-op without motion.
   */
  async setGravityEnabled(on: boolean): Promise<void> {
    if (!this.gravityAvailable || on === this.gravityOn) return;
    if (on) {
      const granted = await DeviceTilt.requestPermission();
      if (!granted) {
        this.gravityOn = false;
        this.panel?.setGravityOn(false);
        return;
      }
      if (!this.tilt) {
        this.tilt = new DeviceTilt(
          (gx, gy) => this.renderer.setGravity(gx, gy),
          () => this.renderer.scatter(150),
          { gMax: 100 },
        );
        this.tilt.start();
      }
      this.gravityOn = true;
      this.panel?.setGravityOn(true);
    } else {
      this.gravityOn = false;
      this.tilt?.stop();
      this.tilt = null;
      this.renderer.setGravity(0, 0);
      this.panel?.setGravityOn(false);
    }
  }

  private async start(): Promise<void> {
    // Renderer first (textures load while audio unlocks).
    await this.renderer.init(this.root, [...DIATOMS]);
    this.renderer.onPick((e) => this.onPick(e));
    // Movement makes sound: drifting diatoms ring when they collide.
    this.renderer.onCollision((a, b, energy) => {
      this.strikeInstance(a, energy, 'col');
      this.strikeInstance(b, energy * 0.85, 'col');
    });

    this.audio = await bootAudio(this.tier.engine, 0xab1e);
    this.samplePlayer = new SamplePlayer(
      this.audio.ctx,
      this.audio.master.ambientInput,
      this.audio.master.sampleInput,
    );
    const budget = ENGINE_BUDGETS[this.tier.engine];
    this.allocator = new VoiceAllocator(budget.maxVoices, this.audio.ctx.sampleRate, budget.modesCap, byId);

    // Envelope -> visuals + allocator feedback.
    this.audio.bank.onEnvelope((snap) => {
      for (const [voiceId, instance] of this.voiceToInstance) {
        const rms = snap.rms[voiceId] ?? 0;
        this.renderer.setVoicePulse(instance, rms);
        this.allocator!.observeRms(voiceId, rms);
        if (rms < 1e-4) this.voiceToInstance.delete(voiceId);
      }
    });
    // Makeup leveler: tap the bank's pre-makeup output (post internal limiter) to measure the actual
    // summed level, regardless of voice count or per-voice loudness.
    this.busTap = this.audio.ctx.createAnalyser();
    this.busTap.fftSize = 1024;
    this.busTapBuf = new Float32Array(this.busTap.fftSize);
    this.audio.bank.node.connect(this.busTap);

    // Gardening panel.
    this.panel = createGardenPanel(
      {
        onDial: (name, value) => this.onDial(name, value),
        onMode: (mode) => {
          this.autonomous = mode === 'generative';
          // Playable = the field settles and waits: stop autonomous intents (above) AND the
          // self-drift, so it's silent until you tap/throw/stir it. Generative drifts + plays itself.
          this.renderer.setDrift(this.autonomous ? 1 : 0);
        },
        onPreset: (id) => {
          if (id.startsWith('user:')) {
            this.loadSceneByName(id.slice(5));
            return;
          }
          const p = PRESETS.find((x) => x.id === id);
          if (p) this.applyPreset(p);
        },
        onVolume: (v) => this.setMasterVolume(v),
        onOpenStudio: () => this.studio?.toggle(),
        onBank: (id) => void this.setExcitationBankById(id),
        onAmbient: (v) => this.setAmbientLevel(v),
        onGravity: (on) => void this.setGravityEnabled(on),
        onDrive: (v) => this.setDrive(v),
        onTune: (param, value) => {
          const p: { targetRms?: number; grainMs?: number; cloudGain?: number; cloudMs?: number } = {};
          p[param] = value;
          this.setGrainParams(p);
        },
        onMidiToggle: () => void this.toggleMidi(),
        onMidiLearn: (id) => {
          this.midi.learn(id);
          this.panel?.setMidiLearning(id);
        },
      },
      PRESETS.map((p) => ({ id: p.id, name: p.name })),
      [
        { id: NO_BANK, name: 'None' },
        ...BANKS.map((b) => ({ id: b.id, name: b.name, attribution: b.attribution })),
      ],
    );
    this.root.appendChild(this.panel.el);
    // Offer the Gravity button only where motion is available (after the panel exists).
    this.detectMotion();
    this.setupMidi();

    // Studio mixer: all diatoms with per-diatom mute/solo/volume/source + master mute-all.
    // Closed by default; opened via the main panel's "Diatoms" button.
    this.studio = createStudioPanel(
      DIATOMS.filter((d) => !d.backgroundLens).map((d) => ({ id: d.id })),
      {
        onMute: (id, m) => this.setDiatomMuted(id, m),
        onSolo: (id, s) => this.setDiatomSolo(id, s),
        onGain: (id, g) => this.setDiatomGain(id, g),
        onExcitation: (id, ex) => this.setDiatomExcitation(id, ex),
        onSaveScene: (name) => this.saveCurrentScene(name),
        onExportScene: (name) => this.exportScene(name),
        onImportScene: (file) => this.importSceneFile(file),
      },
    );
    this.root.appendChild(this.studio.el);
    this.refreshScenes(); // populate the dropdown's User group from localStorage

    // Lookahead scheduler.
    const ctx = this.audio.ctx;
    this.scheduler = new Scheduler(() => ctx.currentTime, {
      update: (now, dt) => {
        this.engine.update(now, dt);
        const st = this.engine.state;
        const space = Math.max(0, Math.min(1, st.units.spaceReverb + this.preset.reverbBias));
        this.renderer.setGlobals(space, st.norm.brightness);
        this.audio!.master.setSpace(space);
        this.audio!.ocean.setLevel(space);
        const mood = st.mood.to;
        if (mood !== this.lastMood) {
          this.lastMood = mood;
          this.panel?.setMood(mood);
        }
        // makeup leveler + live loudness meter
        this.updateLeveler();
        if (this.audio) this.panel?.setMeter(this.audio.master.getMeters());
      },
      collect: (now, horizon) => (this.autonomous ? this.engine.collect(now, horizon) : []),
      fire: (intent) => this.fire(intent),
    });
    this.scheduler.start();

    // Apply the default preset, then play a gentle welcome arpeggio so the visitor hears the
    // instrument immediately (abyssal's long silences make a poor cold-start first impression).
    this.applyPreset(this.preset);
    this.welcome();

    if (import.meta.env.DEV) {
      (window as unknown as { __diatom: unknown }).__diatom = {
        ctx: this.audio.ctx,
        bank: this.audio.bank,
        engine: this.engine,
        master: this.audio.master,
        ocean: this.audio.ocean,
        app: this,
        renderer: this.renderer,
      };
      console.info('[diatom] booted; ctx.state =', this.audio.ctx.state, 'sr =', this.audio.ctx.sampleRate);
    }
  }

  /** Switch the active scene: bias the generative vibe + dominant mood. */
  applyPreset(preset: Preset): void {
    this.preset = preset;
    this.dialVals = { ...preset.dials };
    this.engine.applyDials(preset.dials);
    this.engine.forceMood(preset.mood);
    this.engine.setScale(preset.scale ? SCALES[preset.scale] : undefined);
    this.allocator?.setRatioFamily(preset.ratioFamily);
    this.audio?.master.setDelay(preset.delay?.wet, preset.delay?.timeSec, preset.delay?.feedback);
    this.engine.snapToTargets(); // make the new scene's character immediate, not a 30-60s glide
    this.updateMotion();
    this.panel?.setPreset(preset.id);
    this.panel?.setDials(preset.dials);
    // Master drive (overdrive) follows the preset's tuned default; the Drive control overrides.
    this.setDrive(presetDrive(preset));
    this.panel?.setDrive(this.driveVal);
    // Gravity follows the scene's flag where motion is available (the Gravity button overrides).
    if (this.gravityAvailable) void this.setGravityEnabled(!!preset.gravity);
    // Excite source follows the scene: grain/sampler scenes auto-select their bank so they sound as
    // designed; other scenes reset to None (noise strikes). The user can still pick a bank after.
    void this.setExcitationBankById(preset.excitationBank ?? NO_BANK);
  }

  /** True if this preset actually produces grain/cloud events (so Grain tuning would do something). */
  private presetUsesGrains(p: Preset): boolean {
    return p.excitation.grain > 0 || p.excitation.bow > 0;
  }

  /**
   * Master drive: scales the leveler's output ceiling (1 = default, lower = cleaner, higher = hotter).
   * The actual makeup gain is computed live in updateLeveler() from the measured bus level; drive just
   * scales how loud the leveled output is allowed to get into the compressor/soft-clip.
   */
  setDrive(d: number): void {
    this.driveVal = Math.max(0.3, Math.min(1.3, d));
  }

  // Feed-forward makeup leveler: measure the actual pre-makeup summed bus, then set makeup so the
  // post-makeup level sits at a fixed ceiling regardless of polyphony OR per-voice loudness. This
  // replaces count-based 1/sqrt(N) scaling, which under-corrected few-but-loud presets (e.g. Abyssal
  // drones clipped 53% with the count model — few voices, but each very hot). See the audio-overdrive
  // troubleshooting doc: fix the gain at the offending stage, not downstream of it.
  private updateLeveler(): void {
    if (!this.audio || !this.busTap || !this.busTapBuf) return;
    this.busTap.getFloatTimeDomainData(this.busTapBuf);
    let peak = 0;
    for (let i = 0; i < this.busTapBuf.length; i++) {
      const a = Math.abs(this.busTapBuf[i]);
      if (a > peak) peak = a;
    }
    // Instant attack (catch transients immediately), slow release (don't pump on tails/silences).
    if (peak > this.busPeakFollow) this.busPeakFollow = peak;
    else this.busPeakFollow += (peak - this.busPeakFollow) * BUS_PEAK_RELEASE;

    // makeup = drive · min(BASE, CEILING / busPeak). Sparse/quiet presets keep the full reference
    // makeup (their bus never approaches the ceiling); loud presets get pulled down so the post-makeup
    // peak lands near OUT_CEILING instead of N× over it.
    const follow = Math.max(this.busPeakFollow, BUS_PEAK_FLOOR);
    const makeup = this.driveVal * Math.min(this.audio.master.makeupBase, OUT_CEILING / follow);
    // Fast when reducing (clamp a peak now), slower when releasing (smooth makeup back up).
    const tau = makeup < this.lastMakeup ? 0.02 : 0.12;
    this.lastMakeup = makeup;
    this.audio.master.setMakeup(makeup, tau);
  }

  // ---- Studio mixer controls (called by the studio panel) ----
  setMasterVolume(v: number): void {
    this.masterVolume = v;
    this.audio?.master.setMasterGain(v);
  }
  setDiatomMuted(id: string, muted: boolean): void {
    this.mixer.setMuted(id, muted);
  }
  setDiatomSolo(id: string, solo: boolean): void {
    this.mixer.setSolo(id, solo);
  }
  setDiatomGain(id: string, gain: number): void {
    this.mixer.setGain(id, gain);
  }
  setDiatomExcitation(id: string, ex: 'auto' | 'strike' | 'bow'): void {
    this.mixer.setExcitation(id, ex);
  }

  /**
   * Select the active sound bank (by string id, or NO_BANK). Loaded once, then re-selected on
   * switches. The same recording serves all three bank uses: granular excitation (engine),
   * ambient layer + direct sampler (SamplePlayer). NO_BANK clears all three.
   */
  async setExcitationBankById(id: string): Promise<void> {
    this.activeBank = id;
    // Reflect the selection in the Excite dropdown (it may have been driven by a preset) and gate the
    // Grain tuning sliders: they only do something when a bank is loaded AND the scene fires grain/
    // cloud events (a pure-strike or sampler scene ignores them even with a bank selected).
    this.panel?.setBank(id);
    this.panel?.setGrainTuningActive(id !== NO_BANK && this.presetUsesGrains(this.preset));
    if (id === NO_BANK || !this.audio) {
      this.audio?.bank.setExcitationBank(null);
      this.samplePlayer?.setAmbientBuffer(null);
      return;
    }
    const bank = getBank(id);
    if (!bank) {
      this.audio.bank.setExcitationBank(null);
      this.samplePlayer?.setAmbientBuffer(null);
      return;
    }
    if (!this.loadedBanks.has(id)) {
      try {
        const buf = await loadBankBuffer(this.audio.ctx, bank);
        // Keep a main-thread copy (as an AudioBuffer) for ambient/sampler BEFORE loadExcitation
        // transfers the original to the worklet (transfer detaches it).
        const audio = this.audio.ctx.createBuffer(1, buf.length, this.audio.ctx.sampleRate);
        audio.getChannelData(0).set(buf); // copies synchronously; safe to transfer `buf` afterward
        this.bankAudio.set(id, audio);
        this.audio.bank.loadExcitation(bankNumericId(id), buf);
        this.loadedBanks.add(id);
      } catch (e) {
        console.warn(`[diatom] sound bank '${id}' failed to load`, e);
        // Only clear if this failed load is still the active selection — otherwise we'd wipe a
        // bank the user has since switched (back) to.
        if (this.activeBank === id) {
          this.audio.bank.setExcitationBank(null);
          this.samplePlayer?.setAmbientBuffer(null);
        }
        return;
      }
    }
    // Only apply if the user hasn't switched away while an async load was in flight.
    if (this.activeBank === id) {
      this.audio.bank.setExcitationBank(bankNumericId(id));
      this.samplePlayer?.setAmbientBuffer(this.bankAudio.get(id) ?? null);
    }
  }

  /** Ambient sound-bank layer level (0..1): the active bank's recording as a looping bed. */
  setAmbientLevel(level: number): void {
    this.ambientLevel = Math.max(0, Math.min(1, level));
    this.samplePlayer?.setAmbientLevel(this.ambientLevel);
  }

  /** Live grain-engine tuning (sliders in the panel; captured in saved presets). */
  setGrainParams(p: { targetRms?: number; grainMs?: number; cloudGain?: number; cloudMs?: number }): void {
    Object.assign(this.grainTuning, p);
    this.audio?.bank.setGrainParams(p);
  }

  // ---- Scenes: capture/apply/save/load/export/import ----
  private captureScene(name: string): Scene {
    const diatoms: Scene['diatoms'] = {};
    for (const d of DIATOMS) {
      if (d.backgroundLens) continue;
      diatoms[d.id] = this.mixer.capture(d.id);
    }
    return {
      v: 1,
      name,
      presetId: this.preset.id,
      dials: { ...this.dialVals },
      masterVolume: this.masterVolume,
      diatoms,
      excitationBank: this.activeBank,
      grainTuning: { ...this.grainTuning },
      ambientLevel: this.ambientLevel,
      drive: this.driveVal,
    };
  }

  private applyScene(scene: Scene): void {
    const base = PRESETS.find((p) => p.id === scene.presetId) ?? DEFAULT_PRESET;
    this.applyPreset(base);
    // override with the scene's saved dials + volume
    this.dialVals = { ...scene.dials };
    this.engine.applyDials(scene.dials);
    this.engine.snapToTargets();
    this.updateMotion();
    this.panel?.setDials(scene.dials);
    this.setMasterVolume(scene.masterVolume);
    this.panel?.setVolume(scene.masterVolume);
    // per-diatom mixer state
    this.mixer.load(scene.diatoms);
    this.studio?.setState(scene.diatoms);
    // restore the grain bank (back-compatible: absent = none)
    const bank = scene.excitationBank ?? NO_BANK;
    this.panel?.setBank(bank);
    void this.setExcitationBankById(bank);
    // restore grain tuning (back-compatible: absent = leave current)
    if (scene.grainTuning) {
      this.setGrainParams(scene.grainTuning);
      this.panel?.setTuning(scene.grainTuning);
    }
    // restore ambient layer level (back-compatible: absent = 0)
    const amb = scene.ambientLevel ?? 0;
    this.setAmbientLevel(amb);
    this.panel?.setAmbient(amb);
    // restore master drive (back-compatible: absent = leave the preset's default)
    if (typeof scene.drive === 'number') {
      this.setDrive(scene.drive);
      this.panel?.setDrive(this.driveVal);
    }
  }

  private refreshScenes(): void {
    this.panel?.setUserScenes(listScenes().map((s) => s.name));
  }

  saveCurrentScene(name: string): void {
    const clean = name.trim();
    if (!clean) return;
    saveScene(this.captureScene(clean));
    this.refreshScenes();
    this.panel?.setPreset(`user:${clean}`);
  }

  loadSceneByName(name: string): void {
    const s = getScene(name);
    if (s) {
      this.applyScene(s);
      this.panel?.setPreset(`user:${name}`);
    }
  }

  exportScene(name: string): void {
    const scene = this.captureScene(name.trim() || 'diatom-scene');
    const blob = new Blob([JSON.stringify(scene, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${scene.name}.diatom.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  importSceneFile(file: File): void {
    file
      .text()
      .then((text) => {
        const scene = parseScene(text);
        if (!scene) return;
        saveScene(scene);
        this.refreshScenes();
        this.applyScene(scene);
        this.panel?.setPreset(`user:${scene.name}`);
      })
      .catch((e) => console.warn('[diatom] import failed', e));
  }

  /** A dial moved: it shapes the SOUND (generative axes) and, for calm/roaming, the MOTION. */
  private onDial(name: 'calmActive' | 'darkBright' | 'pureRough' | 'roaming', value: number): void {
    this.dialVals[name] = value;
    this.engine.applyDials({ [name]: value });
    if (name === 'calmActive' || name === 'roaming') this.updateMotion();
  }

  /** Diatom drift = preset character modulated live by Calm↔Active (speed) and Roaming (distance). */
  private updateMotion(): void {
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const speed = this.preset.motion.speed * lerp(0.3, 2.2, this.dialVals.calmActive);
    const amp = this.preset.motion.amp * lerp(0.4, 2.0, this.dialVals.roaming);
    this.renderer.setMotion(speed, amp);
  }

  /** A few soft, staggered strikes on entry so the soundscape announces itself. */
  private welcome(): void {
    if (!this.audio) return;
    const pf = this.engine.state.pitchField;
    const bodies = DIATOMS.filter((d) => !d.backgroundLens);
    [0, 650, 1300, 2100].forEach((ms, i) => {
      setTimeout(() => {
        if (!this.audio) return;
        const desc = bodies[Math.floor(this.rng() * bodies.length)];
        const ratio = pf.ratios[i % pf.ratios.length];
        const octave = Math.round(1.0 - desc.relSize);
        this.fire({
          whenSec: this.audio.ctx.currentTime,
          diatomId: desc.id,
          excitation: 'strike',
          energy: 0.45 + 0.1 * this.rng(),
          position: { x: 0.3 + 0.4 * this.rng(), y: 0.4 },
          pitchHz: pf.rootHz * ratio * Math.pow(2, octave),
          snapped: true,
          decayScale: 1.5,
          durationHintSec: 3,
        });
      }, ms);
    });
  }

  /** Generative path: apply the preset, choose excitation, pick a sprite to flash. */
  private fire(intent: StrikeIntent): void {
    // Solo: when any diatoms are soloed, route autonomous events to a soloed one so it's
    // actually heard (otherwise the generative picks others that the audible() gate silences).
    if (this.mixer.soloing && !this.mixer.isSoloed(intent.diatomId)) {
      const soloed = this.mixer.soloedIds();
      intent.diatomId = soloed[Math.floor(this.rng() * soloed.length)];
    }
    if (this.preset.octaveShift !== 0) intent.pitchHz *= Math.pow(2, this.preset.octaveShift);
    intent.decayScale *= this.preset.decayScale;
    const excitation = pickExcitation(this.preset, this.rng());
    const candidates = this.renderer.instancesForDiatom(intent.diatomId);
    const instance = candidates.length ? candidates[Math.floor(this.rng() * candidates.length)] : -1;
    this.dispatchPlay(intent, excitation, instance);
    if (excitation === 'strike' && instance >= 0) {
      this.scheduleMultihit(instance, intent.energy, 'gen');
    }
  }

  /**
   * Collision / pointer path: excite a specific sprite now, using the preset's excitation
   * palette (so e.g. a collision in Abyssal Drones is a bowed swell, not a bright clink) —
   * this keeps each scene's character consistent across autonomous, collision, and tap events.
   */
  private strikeInstance(
    instanceIndex: number,
    energy: number,
    source = 'tap',
    opts?: { forceStrike?: boolean; allowMultihit?: boolean },
  ): void {
    const diatomId = this.renderer.diatomIdAt(instanceIndex);
    const desc = diatomId ? byId(diatomId) : undefined;
    if (!desc || !this.audio) return;
    const pf = this.engine.state.pitchField;
    const ratio = pf.ratios[Math.floor(this.rng() * pf.ratios.length)];
    const octave = Math.round(1.0 - desc.relSize) + this.preset.octaveShift;
    const pos = this.renderer.instancePosition(instanceIndex);
    const vp = this.renderer.viewport;
    const excitation = opts?.forceStrike ? 'strike' : pickExcitation(this.preset, this.rng());
    this.dispatchPlay(
      {
        whenSec: this.audio.ctx.currentTime,
        diatomId: desc.id,
        excitation: excitation === 'bow' ? 'bow' : 'strike',
        energy,
        position: pos ? { x: pos.x / vp.w, y: pos.y / vp.h } : { x: 0.5, y: 0.5 },
        pitchHz: pf.rootHz * ratio * Math.pow(2, octave),
        snapped: true,
        decayScale: this.preset.decayScale,
        durationHintSec: 1.5,
      },
      excitation,
      instanceIndex,
      source,
    );
    if (excitation === 'strike' && (opts?.allowMultihit ?? true)) {
      this.scheduleMultihit(instanceIndex, energy, source);
    }
  }

  /** Spawn the preset's multihit pattern as forced-strike follow-ups (no further multihit). */
  private scheduleMultihit(instanceIndex: number, energy: number, source: string): void {
    const cfg = this.preset.multihit;
    if (!cfg || cfg.pattern === 'none') return;
    for (const hit of multihitHits(cfg, this.rng)) {
      setTimeout(
        () => this.strikeInstance(instanceIndex, energy * hit.energyScale, source, {
          forceStrike: true,
          allowMultihit: false,
        }),
        Math.max(0, hit.offsetSec * 1000),
      );
    }
  }

  /** DEV-only event counter (window.__ev) for instrumenting dispatch from the console. */
  private countEvent(source: string, kind: string): void {
    const w = window as unknown as { __ev?: Record<string, number> };
    w.__ev = w.__ev ?? {};
    const k = `${source}:${kind}`;
    w.__ev[k] = (w.__ev[k] ?? 0) + 1;
  }

  /** Core: allocate a voice, set modes, excite (bow/strike), and light the sprite. */
  private dispatchPlay(intent: StrikeIntent, excitation: string, instanceIndex: number, source = 'gen'): void {
    if (!this.allocator || !this.audio) return;
    // Studio mixer: mute/solo gating + per-diatom gain & excitation override.
    if (!this.mixer.audible(intent.diatomId)) return;
    const pd = this.mixer.entry(intent.diatomId);
    if (pd) {
      intent.energy *= pd.gain;
      if (pd.excitation !== 'auto') excitation = pd.excitation;
    }
    // Zones: shift pitch by vertical screen position (top higher, bottom lower).
    if (this.preset.zoning) {
      intent.pitchHz *= Math.pow(2, (0.5 - intent.position.y) * 2 * this.preset.zoning);
    }
    // Direct sampler: play a one-shot slice of the active bank instead of ringing a resonator.
    // No bank loaded -> fall back to a strike (never silent).
    if (excitation === 'sampler') {
      const buf = this.bankAudio.get(this.activeBank);
      if (buf && this.samplePlayer) {
        if (import.meta.env.DEV) this.countEvent(source, 'sampler');
        this.samplePlayer.playOneShot(buf, {
          offset01: this.rng(),
          gain: intent.energy,
          pan: intent.position.x * 2 - 1,
        });
        if (instanceIndex >= 0) this.renderer.applyStrike(instanceIndex, intent.energy, intent.pitchHz);
        return;
      }
      excitation = 'strike';
    }
    if (import.meta.env.DEV) {
      this.countEvent(source, excitation === 'grain' ? 'grain' : excitation === 'bow' ? 'bow' : 'strike');
    }
    intent.excitation = excitation === 'bow' ? 'bow' : 'strike';
    const dispatched = this.allocator.dispatch(intent, this.bowedVoices);
    if (!dispatched) return;
    const { voiceId, modes } = dispatched;
    // Cancel any pending bow-release for a voice that's being reused (prevents a stale
    // timeout from releasing the new note).
    const pending = this.bowTimeouts.get(voiceId);
    if (pending !== undefined) {
      clearTimeout(pending);
      this.bowTimeouts.delete(voiceId);
      this.bowedVoices.delete(voiceId);
    }
    this.audio.bank.setVoiceModes(voiceId, modes, intent.position.x * 2 - 1);

    if (excitation === 'bow') {
      this.audio.bank.bow(voiceId, 0.6 + 0.4 * intent.energy);
      this.bowedVoices.add(voiceId);
      const [lo, hi] = this.preset.bowHold;
      const hold = (lo + (hi - lo) * this.rng()) * 1000;
      const t = setTimeout(() => {
        this.audio?.bank.release(voiceId);
        this.bowedVoices.delete(voiceId);
        this.bowTimeouts.delete(voiceId);
      }, hold);
      this.bowTimeouts.set(voiceId, t);
    } else if (excitation === 'grain') {
      // Granular: a slice of the active ocean recording excites the bank. Falls back to a
      // noise strike inside the engine when no bank is active, so it's never silent.
      this.audio.bank.strikeGrain(voiceId, intent.energy);
    } else {
      this.audio.bank.strike(voiceId, intent.energy);
    }

    if (instanceIndex >= 0) {
      this.voiceToInstance.set(voiceId, instanceIndex);
      const fund = modes.length ? Math.min(...modes.map((m) => m.freq)) : intent.pitchHz;
      this.renderer.applyStrike(instanceIndex, intent.energy, fund);
    }
  }

  /** Pointer pick -> strike that sprite immediately; reveal its row if the studio is open. */
  private onPick(e: PickEvent): void {
    this.strikeInstance(e.instanceIndex, 0.9);
    if (this.studio?.isOpen()) this.studio.reveal(e.diatomId);
  }
}
